import { useMemo, useState } from 'react'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import TagRoundedIcon from '@mui/icons-material/TagRounded'
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded'
import type { Channel } from '../../../types/chat'
import { useNls } from '../../../nls/nlsProvider'
import { TextInput } from '../../Common'
import { FloatingInput } from '../../Common/FloatingInput'
import { MemberActionCard, MemberEmptyState } from '../../Member/controls/MemberControlsPrimitives'
import { maskWhatsappNumber } from '../../../utils/whatsappChat'

type Props = {
  channels: Channel[]
  unreadByChannel: Record<string, boolean>
  onSelect: (channelId: string) => void
}

type SectionKey = 'PINNED' | 'CHANNELS' | 'DMS' | 'WHATSAPP'

const isPinnedChannel = (channel: Channel) => channel.id === 'workspace-chat' || channel.slug === 'general'
const isDmChannel = (channel: Channel) => channel.type === 'private' && channel.slug.startsWith('dm-')
const isWhatsappChannel = (channel: Channel) => channel.identityType === 'WHATSAPP'

const relativeActivity = (value: unknown): string => {
  const date = value && typeof value === 'object' && 'toDate' in value
    ? (value as { toDate: () => Date }).toDate()
    : null
  if (!date || Number.isNaN(date.getTime())) return 'Ready for updates'
  const delta = Date.now() - date.getTime()
  if (delta < 60_000) return 'Active just now'
  if (delta < 3_600_000) return `Active ${Math.max(1, Math.round(delta / 60_000))}m ago`
  if (delta < 86_400_000) return `Active ${Math.max(1, Math.round(delta / 3_600_000))}h ago`
  return `Active ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
}

export const MemberChannelPicker = ({ channels, unreadByChannel, onSelect }: Props) => {
  const [query, setQuery] = useState('')
  const { t } = useNls()

  const getSectionLabel = (key: SectionKey) => {
    if (key === 'PINNED') return t('NLSChatSectionPinned')
    if (key === 'DMS') return t('NLSChatSectionDMs')
    if (key === 'WHATSAPP') return t('NLSChatSectionWhatsApp')
    return t('NLSChatSectionChannels')
  }

  const groups = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    const matches = channels
      .filter((channel) => !normalized || channel.name.toLowerCase().includes(normalized) || channel.slug.toLowerCase().includes(normalized))
      .sort((a, b) => {
        const aUnread = unreadByChannel[a.id] ? 1 : 0
        const bUnread = unreadByChannel[b.id] ? 1 : 0
        if (aUnread !== bUnread) return bUnread - aUnread
        return a.name.localeCompare(b.name)
      })

    const whatsapp = matches.filter(isWhatsappChannel)
    const pinned = matches.filter((channel) => !isWhatsappChannel(channel) && isPinnedChannel(channel))
    const dms = matches.filter((channel) => !isWhatsappChannel(channel) && !isPinnedChannel(channel) && isDmChannel(channel))
    const regular = matches.filter((channel) => !isWhatsappChannel(channel) && !isPinnedChannel(channel) && !isDmChannel(channel))
    return [
      ['PINNED', pinned] as const,
      ['CHANNELS', regular] as const,
      ['DMS', dms] as const,
      ['WHATSAPP', whatsapp] as const,
    ].filter(([, list]) => list.length > 0)
  }, [channels, query, unreadByChannel])

  return (
    <section className="memberChannelPicker" aria-label="Channels">
      <FloatingInput label={t('NLSMemberChannelSearchPlaceholder')}>
        <TextInput
          type="search"
          value={query}
          onChange={setQuery}
          placeholder={t('NLSMemberChannelSearchPlaceholder')}
          aria-label={t('NLSMemberChannelSearchAria')}
          leadingIcon={<SearchRoundedIcon fontSize="small" />}
        />
      </FloatingInput>
      {groups.length > 0 ? groups.map(([key, list]) => (
        <div key={key} className="memberNexusSection">
          <div className="memberNexusSection__header">
            <span>{getSectionLabel(key)}</span>
            <span>{list.length}</span>
          </div>
          <div className="memberNexusCardList">
            {list.map((channel) => {
              const unread = unreadByChannel[channel.id] === true
              const isPrivate = channel.type === 'private'
              const isWhatsapp = channel.identityType === 'WHATSAPP'
              const sub = isWhatsapp && channel.whatsappE164
                ? maskWhatsappNumber(channel.whatsappE164)
                : relativeActivity(channel.lastActivity)
              const status = unread ? 'Unread' : isWhatsapp ? 'WhatsApp' : isPrivate ? 'Private' : 'Open'
              const statusTone = unread ? 'info' : isWhatsapp ? 'success' : 'neutral'
              return (
                <MemberActionCard
                  key={channel.id}
                  icon={isPrivate ? <LockRoundedIcon /> : <TagRoundedIcon />}
                  title={`#${channel.name}`}
                  sub={sub}
                  status={status}
                  statusTone={statusTone}
                  onClick={() => onSelect(channel.id)}
                  testId="member-channel-picker-card"
                />
              )
            })}
          </div>
        </div>
      )) : channels.length === 0 || query.trim().length === 0 ? (
        <MemberEmptyState
          icon={<ChatBubbleOutlineRoundedIcon />}
          title={t('NLSChatEmptyNoChannels')}
          hint={t('NLSChatEmptyNoChannelsHelperHint')}
        />
      ) : (
        <MemberEmptyState
          icon={<TravelExploreRoundedIcon />}
          title={t('NLSChatNoChannelMatches')}
          hint={t('NLSChatNoChannelMatchesHint')}
        />
      )}
    </section>
  )
}
